import React from "react";
import { User } from "../../model_gen/company";
import { 
  UserIcon,
  MailIcon,
  MoreHorizontalIcon
} from "lucide-react";
import { Button } from "../ui/button";
import { Tooltip } from "../ui/tooltip";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { cn } from "../../lib/utils";
import moment from "moment";

interface UsersTableProps {
  users: User[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  currentPage,
  pageSize,
  onPageChange,
}) => {
  const formatDate = (timestamp: string) => {
    if (!timestamp) return "Unknown";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: string) => {
    if (!timestamp) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<User>[] = [
    {
      key: "name",
      header: "User",
      width: "w-[30%]",
      render: (user) => (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
            {user.picture ? (
              <img 
                src={user.picture} 
                alt={user.name || user.email}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <UserIcon className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
          <div className="text-sm font-gc-reg text-foreground">
            {user.name || user.email || "Unknown"}
          </div>
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
      width: "w-[30%]",
      render: (user) => (
        <div className="flex items-center gap-2">
          <MailIcon className="w-4 h-4 text-muted-foreground" />
          <div className="text-sm font-gc-reg text-foreground">
            {user.email}
          </div>
        </div>
      ),
    },
    {
      key: "created_at",
      header: "Created",
      width: "w-[20%]",
      render: (user) => (
        user.created_at ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(user.created_at)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(user.created_at)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">Unknown</span>
        )
      ),
    },
    {
      key: "last_login",
      header: "Last Login",
      width: "w-[15%]",
      render: (user) => (
        <div className={cn(
          "text-sm font-gc-reg",
          user.last_login ? "text-foreground" : "text-muted-foreground"
        )}>
          {user.last_login ? formatDate(user.last_login) : "Never"}
        </div>
      ),
    },
    {
      key: "user_id",
      header: "Actions",
      width: "w-[5%]",
      render: () => (
        <Tooltip content="Contact support@trymaitai.ai to remove users">
          <Button
            variant="ghost"
            size="sm"
            disabled
            className="gap-1"
          >
            <MoreHorizontalIcon className="w-3 h-3" />
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <PaginatedTable
      data={users}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      emptyTitle="No users found"
      emptyDescription="Contact support@trymaitai.ai to invite users to your organization."
      showPagination={false} // Users table doesn't need pagination for now
      loadingRows={5}
    />
  );
};

export default UsersTable; 